import Head from 'next/head'
import { useRouter } from 'next/router'
import { formatUrl, prefixLocale } from '@helpers/env'
import { objectKeys } from '@helpers/utils'
import { LOCALES, PRODUCTION_PATH, STAGING_PATH } from '@consts/index'
import Translations from '@helpers/translations'

type CustomHeadProps = {
  desc?: string
  title?: string
  includeNoFollow: boolean
  image?: {
    src?: string
    type?: string
    width: string | number
    height: string | number
  }
}

const basePath =
  process.env.API_ENV === 'production' ? PRODUCTION_PATH : STAGING_PATH

function getCanonicalUrl(path: string) {
  return `${basePath}${prefixLocale()}${path}`
}

function getHrefLangUrl(
  path: string,
  lang: (typeof LOCALES)[keyof typeof LOCALES] = 'en-GB'
) {
  const locale = objectKeys(LOCALES).find(key => LOCALES[key] === lang)
  const prefix = locale === 'GB' ? '' : `/${locale?.toLowerCase()}`
  return `${basePath}${prefix}${path}`
}

const CustomHead = ({
  desc,
  title,
  image,
  includeNoFollow = false
}: CustomHeadProps) => {
  const { asPath } = useRouter()
  const path = !/new-user|index/.test(asPath) ? formatUrl(asPath) : ''
  const pageUrl = getCanonicalUrl(path)
  const refactoredTitle = title
    ? `${title} - SPOKE`
    : `${Translations.localise('common.seo.site-title')}`

  const description =
    desc || `${Translations.localise('common.seo.site-description')}`

  const shouldIncludeNoFollow =
    includeNoFollow || process.env.API_ENV !== 'production'

  return (
    <Head>
      {shouldIncludeNoFollow && (
        <meta name="robots" content="nofollow, noindex" key="spoke-nofollow" />
      )}
      <meta property="og:type" content="website" key="spoke-website" />
      <meta property="og:site_name" content="SPOKE" key="spoke-site_name" />
      <link
        rel="icon"
        type="image/png"
        href="/static/favicon.ico"
        key="spoke-icon"
      />
      <link
        rel="apple-touch-icon"
        href="/static/apple-touch-favicon.png"
        key="spoke-apple-touch-icon"
      />
      <title key="title">{refactoredTitle}</title>
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=2, user-scalable=1, viewport-fit=cover, interactive-widget=overlays-content"
      />
      <meta name="description" content={description} key="description" />
      <meta property="og:title" content={refactoredTitle} key="og:title" />
      <meta
        property="og:description"
        content={description}
        key="og:description"
      />
      {!!image?.src && <meta property="og:image" content={image.src} />}
      {!!image?.type && <meta property="og:image:type" content={image.type} />}
      {!!image?.width && (
        <meta property="og:image:width" content={`${image.width}`} />
      )}
      {!!image?.height && (
        <meta property="og:image:height" content={`${image.height}`} />
      )}
      <meta property="og:url" content={pageUrl} key="og:url" />
      <link rel="canonical" href={pageUrl} key="canonical" />
      <link
        rel="alternate"
        href={getHrefLangUrl(path, 'en-GB')}
        hrefLang="x-default"
        key="alternate"
      />

      {objectKeys(LOCALES).map(locale => (
        <link
          key={`hrefLang-${locale}`}
          rel="alternate"
          hrefLang={LOCALES[locale]}
          href={getHrefLangUrl(path, LOCALES[locale])}
        />
      ))}
    </Head>
  )
}

export default CustomHead
